import { MutationFunctionOptions, OperationVariables, DataProxy, FetchMoreOptions, FetchMoreQueryOptions } from '@apollo/client';
import { DataProps, graphql, MutateProps, OperationOption } from '@apollo/client/react/hoc';
import { DocumentNode } from 'graphql';
import uniqBy from 'lodash/uniqBy';

import { DEFAULT_INFINITE_LIST } from 'src/app/constants/graphql';
import { USER_SCORE } from 'src/app/constants/video';
import { InfiniteList, InfiniteOptions, InfiniteProvider } from 'src/app/models/graphql';
import { Live, LIVE_FRAGMENT } from 'src/app/models/live';
import { Playlist } from 'src/app/models/playlist';
import { PROGRAM_SUBSCRIBE_COUNT_FRAGMENT, PROGRAM_SUBSCRIBE_FRAGMENT, ProgramWithSubscribe, ProgramWithSubscribeCount } from 'src/app/models/program';
import { Video, VIDEO_FRAGMENT, VIDEO_SCORE_FRAGMENT, VideoWithScore } from 'src/app/models/video';
import { UserPlaylists, USER_PLAYLIST_FRAGMENT } from 'src/app/models/user';

export function getFetchMoreOptions<TData, TGraphQLVariables>(document: DocumentNode, customOptions: InfiniteOptions<TData>, currentList: InfiniteList, variables: OperationVariables): FetchMoreQueryOptions<any, any> & FetchMoreOptions<TData, TGraphQLVariables> {
  return {
    query: document,
    updateQuery: (previousResult, { fetchMoreResult }) => {
      if (!fetchMoreResult) return previousResult;

      const previousList = customOptions.getPagedField(previousResult) || DEFAULT_INFINITE_LIST;
      const newList = customOptions.getPagedField(fetchMoreResult) || DEFAULT_INFINITE_LIST;
      const previousItems = previousList.items;
      const newItems = newList.items;
      const allItems = [...previousItems, ...newItems];
      const uniqueItems = uniqBy(allItems, 'id');

      newList.items = uniqueItems;
      return fetchMoreResult;
    },
    variables: {
      ...variables,
      cursor: currentList.nextCursor
    }
  };
}

// TODO
// Replace this with helper from watchlist mutation
export function getWatchlistMutationOptions(video: Video, optimisticIsInWatchlist: boolean): MutationFunctionOptions {
  return {
    optimisticResponse: {
      addVideoToWatchlist: {
        __typename: 'GQLVideo',
        isInWatchlist: optimisticIsInWatchlist
      }
    },
    update: (store, { data }) => {
      const videoFragment = readVideoFragment(store, video.id);

      if (data && videoFragment) {
        writeVideoFragment(store, {
          ...videoFragment,
          isInWatchlist: data.addVideoToWatchlist.isInWatchlist
        });
      }
    },
    variables: {
      id: video.id
    }
  };
}

export function readVideoFragment(store: DataProxy, videoId: string): Video | null {
  return store.readFragment({
    id: 'GQLVideo:' + videoId,
    fragment: VIDEO_FRAGMENT
  });
}

export function writeVideoFragment(store: DataProxy, video: Video): void {
  store.writeFragment({
    data: {
      __typename: 'GQLVideo',
      duration: video.duration,
      id: video.id,
      isInWatchlist: video.isInWatchlist,
      title: video.title,
      viewCount: video.viewCount,
      inPlaylists: video.inPlaylists
    },
    fragment: VIDEO_FRAGMENT,
    id: 'GQLVideo:' + video.id
  });
}

export function getSubscribeMutationOptions(program: ProgramWithSubscribe, optimisticIsSubscribed: boolean, optimisticSubscriberCount?: number): MutationFunctionOptions {
  const subscribeKeyName = optimisticIsSubscribed ? 'subscribe' : 'unsubscribe';
  return {
    optimisticResponse: {
      [subscribeKeyName]: {
        __typename: 'GQLProgram',
        isSubscribed: optimisticIsSubscribed,
        subscriberCount: optimisticSubscriberCount
      }
    },
    update: (store, { data }) => {
      if (data) {
        if (optimisticSubscriberCount || optimisticSubscriberCount === 0) {
          writeProgramWithSubscribeCountFragment(store, {
            ...program,
            isSubscribed: data[subscribeKeyName].isSubscribed,
            subscriberCount: data[subscribeKeyName].subscriberCount
          });
        } else {
          writeProgramWithSubscribeFragment(store, {
            ...program,
            isSubscribed: data[subscribeKeyName].isSubscribed
          });
        }
      }
    },
    variables: {
      programId: program.id
    }
  };
}

export function writeProgramWithSubscribeFragment(store: DataProxy, program: ProgramWithSubscribe): void {
  store.writeFragment({
    data: {
      __typename: 'GQLProgram',
      id: program.id,
      isSubscribed: program.isSubscribed,
      name: program.name,
      slug: program.slug
    },
    fragment: PROGRAM_SUBSCRIBE_FRAGMENT,
    fragmentName: 'ProgramWithSubscribe',
    id: 'GQLProgram:' + program.id
  });
}

export function writeProgramWithSubscribeCountFragment(store: DataProxy, program: ProgramWithSubscribeCount): void {
  store.writeFragment({
    data: {
      __typename: 'GQLProgram',
      id: program.id,
      isSubscribed: program.isSubscribed,
      name: program.name,
      slug: program.slug,
      subscriberCount: program.subscriberCount
    },
    fragment: PROGRAM_SUBSCRIBE_COUNT_FRAGMENT,
    fragmentName: 'ProgramWithSubscribeCount',
    id: 'GQLProgram:' + program.id
  });
}

export function getVideoWithScoreMutationOptions(video: VideoWithScore, optimisticDislike: number, optimisticLike: number, optimisticScore: USER_SCORE): MutationFunctionOptions {
  return {
    optimisticResponse: {
      scoreVideo: {
        __typename: 'GQLVideo',
        score: {
          __typename: 'GQLScore',
          dislike: optimisticDislike,
          like: optimisticLike,
          userScore: optimisticScore
        }
      }
    },
    update: (store, { data }) => {
      const videoFragment = readVideoFragment(store, video.id);
      if (data && videoFragment) {
        writeVideoWithScoreFragment(store, {
          ...videoFragment,
          score: {
            dislike: data.scoreVideo.score.dislike,
            like: data.scoreVideo.score.like,
            userScore: data.scoreVideo.score.userScore
          }
        });
      }
    },
    variables: {
      id: video.id,
      score: optimisticScore
    }
  };
}

export function writeVideoWithScoreFragment(store: DataProxy, video: VideoWithScore): void {
  store.writeFragment({
    data: {
      __typename: 'GQLVideo',
      ...video,
      duration: video.duration,
      id: video.id,
      isInWatchlist: video.isInWatchlist,
      inPlaylists: video.inPlaylists,
      score: {
        __typename: 'GQLScore',
        ...video.score
      },
      title: video.title,
      viewCount: video.viewCount
    },
    fragment: VIDEO_SCORE_FRAGMENT,
    fragmentName: 'VideoWithScore',
    id: 'GQLVideo:' + video.id
  });
}

export function getLiveScoreMutationOptions(live: Live, optimisticDislike: number, optimisticLike: number, optimisticScore: USER_SCORE): MutationFunctionOptions {
  return {
    optimisticResponse: {
      scoreLive: {
        __typename: 'GQLLive',
        score: {
          __typename: 'GQLScore',
          dislike: optimisticDislike,
          like: optimisticLike,
          userScore: optimisticScore
        }
      }
    },
    update: (store, { data }) => {
      if (data) {
        writeLiveScoreFragment(store, {
          ...live,
          score: {
            dislike: data.scoreLive.score.dislike,
            like: data.scoreLive.score.like,
            userScore: data.scoreLive.score.userScore
          }
        });
      }
    },
    variables: {
      id: live.id,
      score: optimisticScore
    }
  };
}

export function writeLiveScoreFragment(store: DataProxy, live: Live): void {
  store.writeFragment({
    data: {
      __typename: 'GQLLive',
      description: live.description,
      id: live.id,
      isCurrentlyLive: live.isCurrentlyLive,
      publishedAt: live.publishedAt,
      score: {
        __typename: 'GQLScore',
        ...live.score
      },
      thumbnail: live.thumbnail,
      title: live.title,
      totalViewCount: live.totalViewCount,
      viewCount: live.viewCount
    },
    fragment: LIVE_FRAGMENT,
    fragmentName: 'Live',
    id: 'GQLLive:' + live.id
  });
}

export function infiniteGraphQL<
  TProps extends TGraphQLVariables | InfiniteProvider | {} = {},
  TData = {},
  TGraphQLVariables = {},
  TChildProps = Partial<DataProps<TData, TGraphQLVariables>> & Partial<MutateProps<TData, TGraphQLVariables>>
>(
  document: DocumentNode,
  customOptions: InfiniteOptions<TData>,
  operationOptions?: OperationOption<TProps, TData, TGraphQLVariables, TChildProps>
): (WrappedComponent: React.ComponentType<any>) => React.ComponentClass<TProps, any> {
  return graphql<TProps, TData>(document, {
    ...operationOptions,
    props: (props) => {
      const dataName = operationOptions && operationOptions.name ? operationOptions.name : 'data';
      const dataResult: typeof props.data = props[dataName];

      if (!dataResult) return props;

      const { fetchMore, variables } = dataResult;
      const currentList = customOptions.getPagedField(dataResult) || DEFAULT_INFINITE_LIST;
      return {
        ...props,
        hasMoreEntries: currentList.items.length < currentList.total,
        loadMoreEntries: () => {
          return fetchMore(getFetchMoreOptions(document, customOptions, currentList, variables));
        }
      };
    }
  });
}

export function readUserPlaylistFragment(store: DataProxy): UserPlaylists | null {
  // Apollo client cache with __typename & id
  // Because we never request id for GQLUser, we set the keyfield into [] so it will cached as __typename:{}
  return store.readFragment({
    id: 'GQLUser:{}',
    fragment: USER_PLAYLIST_FRAGMENT
  });
}

export function writeUserPlaylistFragment(store: DataProxy, playlists: Playlist[]): void {
  store.writeFragment({
    data: {
      __typename: 'GQLUser',
      playlists: playlists
    },
    fragment: USER_PLAYLIST_FRAGMENT,
    id: 'GQLUser:{}'
  });
}
